import React, { useLayoutEffect, useState } from 'react'
import uPlot from 'uplot'

export interface PlotBounds {
  overLeft: number
  overRight: number
  overTop: number
  overBottom: number
}

/**
 * The plot area's rectangle, measured relative to an overlay root that sits on
 * top of the chart. Every overlay that projects a persisted value to a pixel
 * needs it, because `valToPos` returns a position inside `u.over`, not inside
 * the overlay.
 *
 * uPlot is created in a passive effect that runs after this layout effect, and
 * can be recreated later on a recreate-key change; an overlay restoring saved
 * markup typically mounts before the time-series fetch has built uPlot. Nothing
 * else recomputes bounds once uPlot arrives (the ResizeObserver never fires for
 * a uPlot mount), so retry on the next frame until the plot area is measurable
 * — otherwise the overlay keeps `null` bounds forever and never renders, even
 * after a pan/zoom.
 */
export function usePlotBounds(
  uPlotRef: React.RefObject<uPlot | null>,
  rootRef: React.RefObject<HTMLDivElement | null>
): PlotBounds | null {
  const [bounds, setBounds] = useState<PlotBounds | null>(null)

  useLayoutEffect(() => {
    let rafId: number | null = null
    const compute = () => {
      const u = uPlotRef.current
      const root = rootRef.current
      if (!u || !root || !u.over) {
        rafId = requestAnimationFrame(compute)
        return
      }
      const rootRect = root.getBoundingClientRect()
      const overRect = u.over.getBoundingClientRect()
      setBounds({
        overLeft: overRect.left - rootRect.left,
        overRight: overRect.right - rootRect.left,
        overTop: overRect.top - rootRect.top,
        overBottom: overRect.bottom - rootRect.top,
      })
    }
    compute()
    const resizeObserver = new ResizeObserver(compute)
    if (rootRef.current) resizeObserver.observe(rootRef.current)
    return () => {
      resizeObserver.disconnect()
      if (rafId !== null) cancelAnimationFrame(rafId)
    }
  }, [uPlotRef, rootRef])

  return bounds
}
